class PromoGiftPicker extends HTMLElement {
  static CHECKOUT_RETURN_KEY = "ringconn:tradein-checkout";

  connectedCallback() {
    this.dialog = this.querySelector("x-drawer.promo-gift-dialog") || this.querySelector(".promo-gift-dialog");
    this.scroller = this.querySelector("[data-gift-scroller]");
    this.infoScroller = this.querySelector("[data-gift-info]");
    this.maxSelect = Math.max(1, Math.min(3, Number(this.dataset.maxSelect || 3)));
    this._onPointerOpen = false;

    this.addEventListener("click", this._onClick);
    this.dialog?.addEventListener("dialog:after-show", this._onDialogShow);
    this.dialog?.addEventListener("dialog:after-hide", this._onDialogClose);
    this.scroller?.addEventListener("scroll", this._syncInfoScroll, { passive: true });
    this.querySelectorAll(".promo-gift__input").forEach((input) => {
      input.addEventListener("change", this._onInputChange);
    });
    window.addEventListener("pageshow", this._onPageShow);
    this._sync();
    requestAnimationFrame(() => {
      this.classList.add("is-ready");
    });
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._onClick);
    this.dialog?.removeEventListener("dialog:after-show", this._onDialogShow);
    this.dialog?.removeEventListener("dialog:after-hide", this._onDialogClose);
    this.scroller?.removeEventListener("scroll", this._syncInfoScroll);
    this.querySelectorAll(".promo-gift__input").forEach((input) => {
      input.removeEventListener("change", this._onInputChange);
    });
    window.removeEventListener("pageshow", this._onPageShow);
  }

  static resetAll() {
    document.querySelectorAll("promo-gift-picker").forEach((picker) => {
      if (typeof picker.resetSelection === "function") picker.resetSelection();
    });
  }

  resetSelection() {
    let changed = false;
    this.querySelectorAll(".promo-gift__input").forEach((input) => {
      if (input.checked) {
        input.checked = false;
        changed = true;
      }
    });
    if (changed) this._sync();
  }

  _onInputChange = () => {
    this._sync();
  };

  _onPageShow = (event) => {
    const fromTradeInCheckout = sessionStorage.getItem(PromoGiftPicker.CHECKOUT_RETURN_KEY) === "1";
    if (event.persisted || fromTradeInCheckout) {
      sessionStorage.removeItem(PromoGiftPicker.CHECKOUT_RETURN_KEY);
      this.resetSelection();
      return;
    }
    this._sync();
  };

  getSelected() {
    return Array.from(this.querySelectorAll(".promo-gift__input")).filter(
      (input) => input.checked && !input.disabled && input.dataset.available === "true",
    );
  }

  verifyRequired() {
    if (this.dataset.required !== "true") return false;
    const selectable = this.querySelectorAll(".promo-gift__input:not([disabled])");
    if (!selectable.length) return false;
    if (this.getSelected().length) return false;
    this.scrollIntoView({ behavior: "smooth", block: "center" });
    this.classList.add("is-invalid");
    return true;
  }

  _onClick = (event) => {
    const closeBtn = event.target.closest("[data-close-dialog], .promo-gift-dialog__close");
    if (closeBtn) {
      event.preventDefault();
      event.stopPropagation();
      this._closeDialog();
      return;
    }

    const nextBtn = event.target.closest('[is="next-button"]');
    const prevBtn = event.target.closest('[is="prev-button"]');
    if (nextBtn || prevBtn) {
      if (!window.customElements.get("prev-button")) {
        this._step(nextBtn ? 1 : -1);
      }
      return;
    }

    const openBtn = event.target.closest("[data-open-dialog]");
    if (openBtn) {
      event.preventDefault();
      event.stopPropagation();
      this._openDialog(event.pointerType === "mouse" || event.pointerType === "touch");
      return;
    }

    const card = event.target.closest("promo-gift-card");
    if (!card || this.dialog?.contains(event.target)) return;
    if (event.target.closest(".promo-gift__input")) return;
    this._toggleCard(card);
  };

  _toggleCard(card) {
    const input = card.querySelector(".promo-gift__input");
    if (!input || input.disabled || input.dataset.available !== "true") return;
    if (input.checked) {
      input.checked = false;
      this._sync();
      return;
    }
    const selected = this.getSelected();
    if (selected.length >= this.maxSelect) {
      if (this.maxSelect !== 1) return;
      selected.forEach((item) => {
        item.checked = false;
      });
    }
    input.checked = true;
    this._sync();
  }

  _sync() {
    this.classList.remove("is-invalid");
    this.querySelectorAll("promo-gift-card").forEach((card) => {
      const input = card.querySelector(".promo-gift__input");
      card.classList.toggle("is-selected", Boolean(input?.checked));
    });

    const selected = this.getSelected();
    const giftPrice = selected.reduce((sum, input) => sum + Number(input.dataset.price || 0), 0);
    const giftComparePrice = selected.reduce((sum, input) => {
      const compare = Number(input.dataset.comparePrice || 0);
      const price = Number(input.dataset.price || 0);
      return sum + (compare > price ? compare : price);
    }, 0);

    this.dispatchEvent(
      new CustomEvent("promo-gift:change", {
        bubbles: true,
        detail: {
          formId: this.getAttribute("form"),
          affectTotal: true,
          variantIds: selected.map((input) => input.value),
          giftPrice,
          giftComparePrice,
        },
      }),
    );
  }

  _getTargetSlot() {
    const selectedCard = this.querySelector("promo-gift-card.is-selected");
    const firstSlide = this.scroller?.querySelector(".promo-gift-dialog__slide");
    return Number(selectedCard?.dataset.slot || firstSlide?.dataset.slot || 1);
  }

  _syncInfoBySlot(slot) {
    if (!this.infoScroller) return;
    const slides = this.infoScroller.querySelectorAll(".promo-gift-dialog__info-slide");
    let matched = false;

    slides.forEach((slide) => {
      const active = Number(slide.dataset.slot) === Number(slot);
      slide.classList.toggle("is-active", active);
      if (active) matched = true;
    });

    if (!matched && slides[0]) {
      slides[0].classList.add("is-active");
    }
  }

  _openDialog(pointerOpen) {
    if (!this.dialog) return;

    if (this.dialog.open) {
      this._prepareDialogAfterLayout();
      return;
    }

    this._onPointerOpen = pointerOpen;
    this.dialog.classList.toggle("is-pointer-open", pointerOpen);

    if (typeof this.dialog.show === "function") {
      this.dialog.show(false);
    } else {
      this.dialog.setAttribute("open", "immediate");
    }
  }

  _onDialogShow = () => {
    this._prepareDialogAfterLayout();
  };

  _prepareDialogAfterLayout(retry = 0) {
    if ((this.scroller?.clientWidth || 0) <= 0 && retry < 3) {
      requestAnimationFrame(() => this._prepareDialogAfterLayout(retry + 1));
      return;
    }
    this._prepareDialog();
  }

  _getNavButtons() {
    const root = this.dialog || this;
    return {
      prev: root.querySelector('[is="prev-button"]'),
      next: root.querySelector('[is="next-button"]'),
    };
  }

  _getScrollerIndex() {
    if (!this.scroller) return 0;

    const slides = this.scroller.children;
    const count = slides?.length || 0;
    if (!count) return 0;

    const width = this.scroller.clientWidth || 1;
    const scrollLeft = this.scroller.scrollLeft;
    const maxScrollLeft = this.scroller.scrollWidth - width;

    if (scrollLeft <= 0) return 0;
    if (scrollLeft >= maxScrollLeft - 5) return count - 1;

    return Math.min(count - 1, Math.max(0, Math.round(scrollLeft / width)));
  }

  _syncNavState(index = this._getScrollerIndex()) {
    const { prev, next } = this._getNavButtons();
    if (!prev && !next) return;

    const count = this.scroller?.children?.length || 0;
    if (count <= 1) return;

    const last = count - 1;
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= last;
  }

  _scrollScrollerTo(index, { instant = false } = {}) {
    if (!this.scroller) return;

    const slides = Array.from(this.scroller.children || []);
    const target = slides[index];
    if (!target) return;

    const containerWidth = this.scroller.clientWidth;
    const maxScrollLeft = this.scroller.scrollWidth - containerWidth;
    let scrollLeft;

    if (index === slides.length - 1) {
      scrollLeft = maxScrollLeft;
    } else {
      const itemWidth = target.clientWidth;
      scrollLeft = target.offsetLeft - (containerWidth - itemWidth) / 2;
    }

    scrollLeft = Math.max(0, Math.min(scrollLeft, maxScrollLeft));
    this.scroller.scrollTo({
      left: scrollLeft,
      behavior: instant ? "auto" : "smooth",
    });
    this._syncInfoScroll();
    this._syncNavState(index);
  }

  _prepareDialog() {
    this.dialog?.querySelectorAll(".promo-gift-dialog__image").forEach((img) => {
      if (img.loading === "lazy") {
        img.loading = "eager";
      }
      if (!img.complete) {
        img.decode?.().catch(() => {});
      }
    });

    this.infoScroller?.querySelectorAll(".promo-gift-dialog__info-copy").forEach((copy) => {
      copy.scrollTop = 0;
    });

    const slot = this._getTargetSlot();
    const slides = Array.from(this.scroller?.children || []);
    const index = Math.max(
      0,
      slides.findIndex((slide) => Number(slide.dataset.slot) === slot),
    );

    this._scrollScrollerTo(index, { instant: true });
    this._syncInfoBySlot(Number(slides[index]?.dataset.slot || slot));
    requestAnimationFrame(() => this._syncNavState(index));
  }

  _closeDialog = () => {
    if (!this.dialog?.open) return;
    if (typeof this.dialog.hide === "function") {
      this.dialog.hide();
    } else {
      this.dialog.removeAttribute("open");
    }
    this.dialog.classList.remove("is-pointer-open");
  };

  _onDialogClose = () => {
    this.dialog?.classList.remove("is-pointer-open");
    this._syncNavState(0);
  };

  _step(direction) {
    if (!this.scroller) return;
    const maxIndex = Math.max(0, (this.scroller.children?.length || 1) - 1);
    const index = Math.min(maxIndex, Math.max(0, this._getScrollerIndex() + direction));
    this._scrollScrollerTo(index);
  }

  _syncInfoScroll = () => {
    if (!this.infoScroller || !this.scroller) return;
    const index = this._getScrollerIndex();
    const slot = Number(this.scroller.children[index]?.dataset.slot || 0);
    if (slot) this._syncInfoBySlot(slot);
    this._syncNavState(index);
  };
}

window.PromoGiftPicker = PromoGiftPicker;

if (!window.customElements.get("promo-gift-picker")) {
  window.customElements.define("promo-gift-picker", PromoGiftPicker);
}

if (!window.customElements.get("promo-gift-card")) {
  window.customElements.define("promo-gift-card", class extends HTMLElement {});
}
